import { Fragment, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Circle, XCircle, Clock, ArrowUpCircle, MinusCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { act, Pill, statusTone } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ICON = { approved: CheckCircle2, rejected: XCircle, pending: Clock, waiting: Circle, escalated: ArrowUpCircle, skipped: MinusCircle };
const COLOR = { approved: "text-emerald-600", rejected: "text-red-600", pending: "text-amber-500", waiting: "text-slate-300", escalated: "text-orange-500" };

function ItemSummary({ a }) {
  const it = a.item || {};
  const rows = {
    cr: [["Authorisation", it.tier ? (it.tier === "high" ? "Change approval (high impact)" : "PMO approval (low impact)") : `Cat-${it.category}`], ["Variation type", it.variation_type || "—"],
      ["Why this level", (it.impact_reasons || []).join("; ") || "Within low-impact limits"], ["Budget change", money(it.cost_impact, a.currency)], ["Timeline", `${it.schedule_impact_days || 0} working days`],
      ["Quality impact", { none: "None", criteria_same: "Affected, acceptance criteria unchanged", criteria_change: "Changes acceptance criteria" }[it.quality_impact] || "—"],
      ["Scope / contingency", [it.scope_impact && "Scope change", it.contingency && "Funded from contingency"].filter(Boolean).join(", ") || "—"], ["Justification", it.justification]],
    po: [["Vendor", it.vendor], ["Amount", money(it.amount, a.currency)], ["Description", it.description]],
    expense: [["Amount", money(it.amount, a.currency)], ["Description", it.description]],
    timesheet: [["Week", it.week], ["Hours", it.hours], ["Notes", it.notes]],
    gate: [["Gate", it.gate], ["Submitted by", it.submitted_by_name], ["Escalated", it.escalated ? "Yes — Red/breach" : "No"]],
    acceptance: [["Document", it.name], ["Uploaded by", it.uploaded_by_name]],
    access: [["User", it.user_name], ["Role", it.project_role]],
  }[a.item_type] || [];
  return (
    <dl className="grid grid-cols-3 gap-y-2 text-sm">
      {rows.map(([k, v]) => (<Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="col-span-2 text-slate-800">{v || v === 0 ? v : "—"}</dd></Fragment>))}
    </dl>
  );
}

export default function ApprovalDetail({ id, onClose, onDone }) {
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [rationale, setRationale] = useState("");
  const [conditions, setConditions] = useState("");
  useEffect(() => {
    setA(null);
    setRationale("");
    setConditions("");
    if (id) api.get(`/approvals/${id}`).then((r) => setA(r.data)).catch(() => {});
  }, [id]);
  const step = a?.steps[a.current];
  const canAct = a && a.status === "pending" && a.requester_id !== user.id;
  const decideNow = async (decision) => {
    const r = await act(api.post(`/approvals/${id}/decide`, { decision, rationale, conditions }));
    if (r) {
      toast.success(decision === "reject" ? "Rejected — requester notified" : r.data.status === "approved" ? "Approved — final decision recorded" : "Recorded — routed to next step");
      window.dispatchEvent(new Event("aspire:notifs"));
      onDone?.();
      onClose();
    }
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="approval-detail">
        {!a ? <div className="p-6 text-sm text-slate-500">Loading…</div> : (
          <div className="space-y-6">
            <SheetHeader>
              <div className="overline">{a.item_type} · {a.project_code}</div>
              <SheetTitle className="font-display text-xl">{a.title}</SheetTitle>
              <div className="flex gap-2 text-xs text-slate-500">Raised by {a.requester_name} · <Pill tone={statusTone(a.status)} testid="approval-status">{a.status}</Pill></div>
            </SheetHeader>
            <ItemSummary a={a} />
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Approval route (computed by engine)</div>
              <ol className="space-y-3" data-testid="approval-steps">
                {a.steps.map((s, i) => {
                  const I = ICON[s.status] || Circle;
                  return (
                    <li key={`${s.level}-${s.label}`} className="flex gap-3">
                      <I className={`mt-0.5 h-5 w-5 shrink-0 ${COLOR[s.status] || "text-slate-300"}`} />
                      <div className="text-sm">
                        <div className="font-medium text-slate-800">{s.label} <span className="font-normal text-slate-400">— {s.verb}</span></div>
                        <div className="text-xs text-slate-500">{s.approver_names?.join(", ")}</div>
                        {s.decided_by_name && <div className="mt-1 rounded-md bg-slate-50 p-2 text-xs text-slate-600"><b>{s.decided_by_name}</b>{s.delegate_of && " (delegate)"} · {new Date(s.decided_at).toLocaleString()}<br />{s.rationale}{s.conditions && <><br />Conditions: {s.conditions}</>}</div>}
                        {s.status === "escalated" && <div className="text-xs text-orange-600">Escalated by timer — no action within SLA</div>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
            {canAct && (
              <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                <div className="text-sm font-semibold">Your decision — {step.label}</div>
                <div className="space-y-1.5"><Label>Rationale (mandatory)</Label><Textarea data-testid="decision-rationale" value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="e.g. Impact assessed; within tolerance; recommend approval." /></div>
                <div className="space-y-1.5"><Label>Conditions (optional)</Label><Textarea data-testid="decision-conditions" rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)} /></div>
                <div className="flex gap-2">
                  <Button data-testid="decision-approve-btn" disabled={rationale.trim().length < 3} onClick={() => decideNow("approve")} className="bg-emerald-600 hover:bg-emerald-700">{step.verb === "decide" ? "Approve" : step.verb[0].toUpperCase() + step.verb.slice(1)}</Button>
                  <Button data-testid="decision-reject-btn" disabled={rationale.trim().length < 3} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => decideNow("reject")}>Reject</Button>
                </div>
              </div>
            )}
            {a.status === "pending" && a.requester_id === user.id && <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-800">Segregation of duties: you raised this item, so you cannot decide it.</div>}
            <Link to={`/projects/${a.project_id}`} className="text-sm text-indigo-600 hover:underline" data-testid="approval-open-project">Open project workspace →</Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
